"use client"

import { useState } from "react"
import { Button } from "@ui/shad/button"
import { cn } from "@ui/utils"
import { Download } from "lucide-react"

import type { BeneficiaryReportFormat, BeneficiaryReportSize } from "@/hooks/api/beneficiary-reports/api"
import { useDownloadBeneficiaryReport } from "@/hooks/api/beneficiary-reports/mutations"
import { GenericDialog } from "@/components/dialogs"

const sizes: { value: BeneficiaryReportSize; label: string; description: string; shape: string }[] = [
  { value: "letter", label: "Letter", description: "8.5 × 11 in", shape: "h-9 w-7" },
  { value: "square", label: "Square", description: "1:1", shape: "size-8" },
  { value: "widescreen", label: "Widescreen", description: "16:9", shape: "h-6 w-10" },
]

export function DownloadBeneficiaryReport({
  clientId,
  accountIds,
  bookId,
}: {
  clientId: string
  accountIds: string[]
  bookId?: string
}) {
  const [open, setOpen] = useState(false)
  const [format, setFormat] = useState<BeneficiaryReportFormat>("pdf")
  const [size, setSize] = useState<BeneficiaryReportSize>("letter")
  const download = useDownloadBeneficiaryReport(bookId)

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Download />
        Beneficiary report
      </Button>
      <GenericDialog
        open={open}
        onOpenChange={setOpen}
        title="Download beneficiary report"
        description={`${accountIds.length} Schwab ${accountIds.length === 1 ? "account" : "accounts"} · Uses your brand kit`}
        action={{
          label: "Download report",
          pending: download.isPending,
          onClick: () => download.mutate({ clientId, accountIds, format, size }, { onSuccess: () => setOpen(false) }),
        }}
      >
        <div className="space-y-4">
          <fieldset className="space-y-2" disabled={download.isPending}>
            <legend className="text-sm font-medium">Format</legend>
            <div className="grid grid-cols-2 gap-2">
              {(["pdf", "png"] as const).map((value) => (
                <Button
                  key={value}
                  variant={format === value ? "primary" : "outline"}
                  aria-pressed={format === value}
                  onClick={() => setFormat(value)}
                >
                  {value === "pdf" ? "PDF document" : "PNG image"}
                </Button>
              ))}
            </div>
          </fieldset>
          <fieldset className="space-y-2" disabled={download.isPending}>
            <legend className="text-sm font-medium">Page size</legend>
            <div className="grid grid-cols-3 gap-2">
              {sizes.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={size === option.value}
                  onClick={() => setSize(option.value)}
                  className={cn(
                    "focus-visible:ring-ring flex flex-col items-center gap-1 rounded-md border p-3 text-sm focus-visible:ring-2 disabled:opacity-50",
                    size === option.value ? "border-primary bg-accent" : "hover:bg-muted"
                  )}
                >
                  <span className="flex h-10 items-center" aria-hidden="true">
                    <span className={cn("bg-background rounded-xs border", option.shape)} />
                  </span>
                  <span className="font-medium">{option.label}</span>
                  <span className="text-muted-foreground text-xs">{option.description}</span>
                </button>
              ))}
            </div>
          </fieldset>
          <p className="text-muted-foreground text-xs">Always one page. Larger reports scale to fit.</p>
        </div>
      </GenericDialog>
    </>
  )
}
